<template>
    <div v-if="!productStore.isMediaclipConfiguratorProduct && hasContent" class="mt-3 mb-3 flex flex-col gap-2.5">
        <!-- Filters -->
        <FilterSelectMenu
            v-for="filter in filters"
            :key="`filter_${filter.meta.name}`"
            :data-test-id="`pdp-filter-${filter.meta.name}`"
            :group="filter"
            :show-prices="pricedSelectorDecision.filterKey === filter.meta.name && !filter.meta.hide_price_in_dropdown"
            :show-placeholder="filterForcingPlaceholder(filter)"
            :data-size-selection-required="filterForcingPlaceholder(filter) || undefined"
            :placeholder="$t('pages.product_detail_page.product_info.product_filters.make_your_choice')"
            :hide-badge="productStore.configurableProduct.is_hiding_most_chosen_label"
            reveal-on-open
            v-model:model-value="productStore.selectedFilterOptions[filter.meta.name]"
            @update:model-value="() => onFilterChange(filter.meta.name)"
        />

        <!-- Product selection -->
        <ProductSelectMenu
            v-if="showProductSelectMenu"
            data-test-id="pdp-size-select"
            :group="productFilter"
            :most-popular-size="productStore.mostPopularProduct"
            :show-placeholder="productStore.forceSizeSelectionActive"
            :data-size-selection-required="productStore.forceSizeSelectionActive || undefined"
            :placeholder="$t('pages.product_detail_page.product_info.product_filters.make_your_choice')"
            v-model:model-value="productStore.selectedProduct"
            :show-prices="pricedSelectorDecision.showProductSelectPrices"
            :hide-badge="productStore.configurableProduct.is_hiding_most_chosen_label"
            reveal-on-open
            @update:model-value="onProductChange"
        />

        <!-- Mediaclip user text -->
        <Input
            v-if="showUserTextInput"
            v-model="productPageStore.mediaclipUserText"
            name="mediaclip_user_text"
            :placeholder="$t('pages.product_detail_page.user_text.placeholder')"
            required
            :label="$t('pages.product_detail_page.user_text.label')"
            :error="productPageStore.showMediaclipUserTextError ? $t('pages.product_detail_page.user_text.error.empty') : undefined"
            @on-input="productPageStore.showMediaclipUserTextError = false"
        />
    </div>
</template>

<script setup lang="ts">
import Input from '@/components/forms/inputs/Input.vue';
import FilterSelectMenu from '@/components/forms/select-menus/FilterSelectMenu.vue';
import ProductSelectMenu from '@/components/forms/select-menus/ProductSelectMenu.vue';
import {
    determinePricedSelectorDecision,
    enrichFilterOptionsWithProductPrices,
    mapProductFilterProductSelectMenu,
    syncSelectedFilterOptionPrices,
} from '@/lib/products/filter';
import { determineSelectedProduct } from '@/lib/products/size';
import { useProductPageStore } from '@/stores/pages/ProductPageStore';
import { useProductStore } from '@/stores/products/ProductStore';
import type {
    ConfigurableProductFilterObject,
    ConfigurableProductProductObject,
    PaazlShippingDataObject,
    ProductDataObject,
} from '@/types/generated';
import ProductShippingOptionsController from '@/types/wayfinder/actions/App/Http/Controllers/Api/Products/ProductShippingOptionsController';
import { useHttp } from '@inertiajs/vue3';
import _ from 'lodash';
import { computed, nextTick, onMounted, ref } from 'vue';

const productPageStore = useProductPageStore();
const productStore = useProductStore();

const productFilter = computed(() => mapProductFilterProductSelectMenu(productStore.allFilteredSizes));

// A single-option filter cannot be opened, so it is dropped as soon as another control does offer a
// choice; on its own it stays, to show what the customer is getting.
const filters = computed<Record<string, ConfigurableProductFilterObject>>(() => {
    const enriched = enrichFilterOptionsWithProductPrices(productStore.filters, productStore.selectedFilterOptions, productStore.availableSizes);
    const entries = Object.entries(enriched);

    const hasChoiceElsewhere =
        entries.some(([, filter]: [string, ConfigurableProductFilterObject]) => filter.options.length > 1) || productStore.hasMoreThanOneFilteredSize;

    if (!hasChoiceElsewhere) {
        return enriched;
    }

    return Object.fromEntries(entries.filter(([, filter]: [string, ConfigurableProductFilterObject]) => filter.options.length > 1));
});

// True when the visible filters alone leave one size, so they already show the chosen format.
// A url filter can also narrow it to one size, but then nothing on screen shows the size.
const visibleFiltersPinVariant = computed<boolean>(() => {
    const matchingSizes = _.filter(productStore.configurableProduct.configurable_product_products, (size: ConfigurableProductProductObject) =>
        _.every(
            filters.value,
            (filter: ConfigurableProductFilterObject) =>
                size.product.data[filter.meta.name as keyof ProductDataObject]?.toString() ===
                productStore.selectedFilterOptions[filter.meta.name]?.value.toString(),
        ),
    );

    return matchingSizes.length <= 1;
});

// A single remaining size still renders so the selected format is visible, but only when nothing
// else already shows it: visible filters that pin the variant, or an AI style, which fixes its own size.
const showProductSelectMenu = computed<boolean>(() => {
    if (productStore.isMediaclipBookProduct) {
        return false;
    }

    if (productStore.hasMoreThanOneFilteredSize) {
        return productStore.allFilteredSizes.length > 0;
    }

    if (productStore.isAiProduct || (Object.keys(filters.value).length > 0 && visibleFiltersPinVariant.value)) {
        return false;
    }

    return productStore.allFilteredSizes.length > 0;
});

const showUserTextInput = computed(() => productStore.isMediaclipLetterCollageProduct && !productPageStore.configurableProduct.mediaclip_user_text);

const hasContent = computed(() => Object.keys(filters.value).length > 0 || showProductSelectMenu.value || showUserTextInput.value);
const pricedSelectorDecision = computed(() => determinePricedSelectorDecision(Object.keys(filters.value), showProductSelectMenu.value));

// Tracks which filter dropdowns the user has explicitly picked from.
// Independent of hasUserSelectedProduct (which only tracks size selection).
const userConfirmedFilters = ref<Record<string, boolean>>({});

// A single-option dropdown is disabled, so never force its placeholder: show the option instead.
const filterForcingPlaceholder = (filter: ConfigurableProductFilterObject): boolean =>
    filter.options.length > 1 &&
    productStore.configurableProduct.force_size_selection &&
    !productStore.hasUserSelectedProduct &&
    !userConfirmedFilters.value[filter.meta.name];

onMounted(() => {
    syncSelectedFilterOptionPrices(filters.value, productStore.selectedFilterOptions);
    productStore.selectedProduct = determineSelectedProduct(productStore.allFilteredSizes, productStore.selectedProduct);
});

const onFilterChange = async (filterName: string) => {
    userConfirmedFilters.value = { ...userConfirmedFilters.value, [filterName]: true };
    // Let the dropdown close paint first before doing the (potentially heavy) re-sync and network work.
    await nextTick();

    syncSelectedFilterOptionPrices(filters.value, productStore.selectedFilterOptions);

    productStore.selectedProduct = determineSelectedProduct(productStore.allFilteredSizes, productStore.selectedProduct);

    await requestShippingOptions();
};

const onProductChange = async () => {
    productStore.applyProduct(productStore.selectedProduct);
    await requestShippingOptions();
};

type ShippingOptionsResponse = PaazlShippingDataObject & { show_delivery_information?: boolean };

const http = useHttp<object, ShippingOptionsResponse>({});

const requestShippingOptions = async () => {
    productPageStore.isRetrievingShippingOptions = true;

    await http.get(
        ProductShippingOptionsController({
            configurableProduct: productStore.configurableProduct.group_identifier,
            productSku: productStore.selectedProduct.product.sku,
        }).url,
        {
            onSuccess: data => {
                productPageStore.shipping = data;
                // An empty payload carries no flag: nothing to show.
                productPageStore.showDeliveryInformation = data.show_delivery_information ?? false;
            },
            onHttpException: () => {
                productPageStore.shipping = undefined;
                productPageStore.showDeliveryInformation = false;
            },
            onError: () => {
                productPageStore.shipping = undefined;
                productPageStore.showDeliveryInformation = false;
            },
            onFinish: () => {
                productPageStore.isRetrievingShippingOptions = false;
            },
        },
    );
};
</script>
